import React, { useState, useEffect } from 'react';
import './App.css';
import HomeyTable from './HomeyTable';
import Header from './Header';
import HomeyMap from './HomeyMap';
import { initializeCloudApi, cloudApi } from './util/fetchHomeys.js';

function App() {
    const [homeys, setHomeys] = useState(null);
    const [user, setUser] = useState(null);
    const [view, setView] = useState('list');
    const [error, setError] = useState(null);
    const [isInstaller, setIsInstaller] = useState(false);
    const [installerEmail, setInstallerEmail] = useState('');
    const [installerPhone, setInstallerPhone] = useState('');
    const [installerWebsite, setInstallerWebsite] = useState('');
    const [installerInformationOpen, setInstallerInformationOpen] = useState(false);
    const [isSavingInstallerInformation, setIsSavingInstallerInformation] = useState(false);

    useEffect(() => {
        const fetchHomeysData = async () => {
            const authenticatedUser = await initializeCloudApi();
            if (authenticatedUser) {
                setUser(authenticatedUser);
                setHomeys(authenticatedUser.homeys.filter(homey => {
                    const homeyUserMe = homey.users.find(user => user.userId === authenticatedUser.id);
                    if (!homeyUserMe) return false;

                    return homeyUserMe.role === 'installer';
                }));
                // Check if the user is actually an installer, if not show error states and message to turn them away
                const isInstallerRole = authenticatedUser.roleIds.includes('installer');
                setIsInstaller(isInstallerRole);
                if (!isInstallerRole) setView('error');

                setInstallerEmail(authenticatedUser.installerEmail || '');
                setInstallerPhone(authenticatedUser.installerPhone || '');
                setInstallerWebsite(authenticatedUser.installerWebsite || '');

                if(!authenticatedUser.installerEmail && !authenticatedUser.installerPhone && !authenticatedUser.installerWebsite) {
                    setInstallerInformationOpen(true);
                }
            }
        };

        fetchHomeysData().catch(error => {
            console.error(error);
            setError(error.message);
        });
    }, []);

    const handleSaveInstallerInformation = async (e) => {
        e.preventDefault();

        setIsSavingInstallerInformation(true);

        Promise.resolve().then(async () => {
            await cloudApi.updateInstallerInformation({
                $validate: false, // Errors when values are set to `null`
                userId: user.id,
                email: installerEmail || null,
                phone: installerPhone || null,
                website: installerWebsite || null,
            });
        })
        .then(() => {
            setInstallerInformationOpen(false);
        })
        .catch(err => {
            alert(err.message);
        }).finally(() => {
            setIsSavingInstallerInformation(false);
        });
    }


    return (
        <div className="App">
            <Header
                isInstaller={isInstaller}
                user={user}
                setView={setView}
                setInstallerInformationOpen={setInstallerInformationOpen}
                view={view}
            />
            
            {error && (
                <div className="CenteredMessageWrapper">
                    <div className="CenteredMessage">
                        <p>Error: {error}</p>
                    </div>
                </div>
            )}

            {!error && user && isInstaller && view === 'list' && (
                <HomeyTable homeys={homeys} />
            )}

            {!error && user && isInstaller && view === 'map' && (
                <HomeyMap homeys={homeys} />
            )}

            {!error && user === null && (
                <div className="CenteredMessageWrapper">
                    <div className="CenteredMessage">
                        <p>Loading...</p>
                    </div>
                </div>
            )}

            {(!error && user && !isInstaller) && (
                <div className="CenteredMessageWrapper">
                    <div className="CenteredMessage">
                        <p>
                            You don't have access to Homey for Installers.<br /><a href="https://homey.app/en-us/wiki/homey-for-installers/" target="_blank" rel="noreferrer">Learn more »</a>
                        </p>
                    </div>
                </div>
            )}

            {user && isInstaller && installerInformationOpen && (
                <div className="InstallerInformationWrapper"
                    onClick={() => {
                        if(!installerEmail && !installerPhone && !installerWebsite) return;
                        setInstallerInformationOpen(false);
                    }}
                >
                    <form className="InstallerInformation"
                        onClick={e => e.stopPropagation()}
                        onSubmit={handleSaveInstallerInformation}
                    >
                        <h1 className="InstallerInformation-Title">Installer Information</h1>
                        <p className="InstallerInformation-Subtitle">This information will be visible to your customers.</p>

                        <div className="InstallerInformation-Field">
                            <label htmlFor="installer-email">Email</label>
                            <input id="installer-email"
                                type="email"
                                value={installerEmail}
                                placeholder="john@example.com"
                                onChange={e => setInstallerEmail(e.target.value)}
                            />
                        </div>

                        <div className="InstallerInformation-Field">
                            <label htmlFor="installer-phone">Phone</label>
                            <input id="installer-phone"
                                type="tel"
                                value={installerPhone}
                                placeholder="+31612345678"
                                pattern="^\+[1-9]\d{1,14}$"
                                onChange={e => setInstallerPhone(e.target.value)}
                                onInvalid={e => e.target.setCustomValidity('Enter a valid phone number, starting with the country code, and no spaces, e.g. +31640923136.')}
                                onInput={e => e.target.setCustomValidity('')}
                            />
                        </div>

                        <div className="InstallerInformation-Field">
                            <label htmlFor="installer-website">Website</label>
                            <input id="installer-website"
                                type="text"
                                value={installerWebsite}
                                placeholder="https://example.com"
                                pattern="^https:\/\/[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}.*$"
                                onChange={e => setInstallerWebsite(e.target.value)}
                                onInvalid={e => e.target.setCustomValidity('Enter a valid website URL, starting with https://.')}
                                onInput={e => e.target.setCustomValidity('')}
                            />
                        </div>

                        <button type="submit" className={`InstallerInformation-Button ${isSavingInstallerInformation ? 'is-loading' : ''}`}>Save</button>
                    </form>
                </div>
            )}
        </div>
    );
}

export default App;
